import React, { useEffect, useState } from "react";
import KeplerGl from "kepler.gl";
import { useDispatch, useSelector } from "react-redux";
import { addDataToMap, resetMapConfig } from "kepler.gl/actions";
import { getVeloxityAudienceLocationHeat } from "../../../slices/veloxity/audience/LocationHeatReducer";
import { showErrorAlert } from "../../../components/custom/sweetalertHelper";
import { Card, CardHeader, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledDropdown, CardBody } from "reactstrap";

const processLocations = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    console.warn("Locations array is empty or invalid.");
    return [];
  }

  return locations.map((loc, index) => {
    const latitude = loc.latitude ?? 0; // Nullish coalescing ile varsayılan değer
    const longitude = loc.longitude ?? 0;
    const deviceCount = loc.deviceCount ?? 1;

    if (latitude === undefined || longitude === undefined) {
      console.error(`Invalid data at index ${index}:`, loc);
    }

    return [latitude, longitude, deviceCount];
  });
};

const HeatMapCard = ({ audienceId }) => {
  const dispatch = useDispatch();
  const locations = useSelector((state) => state.locationHeat.payload.locations || []);
  const [gridType, setGridType] = useState("HOME");
  const [mapKey, setMapKey] = useState(Date.now());
  const [isResetting, setIsResetting] = useState(false);

  const handleSelectionChange = (newGridType) => {
    setIsResetting(true);
    dispatch(resetMapConfig());
    setTimeout(() => {
      setGridType(newGridType);
      setMapKey(Date.now());
      setIsResetting(false);
    }, 100);
  };

  useEffect(() => {
    if (audienceId && !isResetting) {
      dispatch(getVeloxityAudienceLocationHeat({ audienceId, gridType }));
    } else if (!audienceId) {
      showErrorAlert("Error", "No audienceId provided. Cannot fetch data.");
    }
  }, [audienceId, dispatch, gridType, isResetting]);

  useEffect(() => {
    if (locations.length > 0 && !isResetting) {
      const processedData = processLocations(locations);

      if (processedData.length === 0) {
        console.warn("Processed data is empty. Skipping map update.");
        return;
      }

      dispatch(
        addDataToMap({
          datasets: {
            info: { label: "Heatmap Dataset", id: `heatmap_dataset_${gridType}` },
            data: {
              fields: [
                { name: "latitude", type: "real" },
                { name: "longitude", type: "real" },
                { name: "deviceCount", type: "integer" },
              ],
              rows: processedData,
            },
          },
          option: { centerMap: true },
        })
      );
    } else if (locations.length === 0 && !isResetting) {
      console.warn("Locations are empty. Resetting map.");
      dispatch(resetMapConfig());
    }
  }, [locations, dispatch, gridType, isResetting]);

  return (
    <Card>
      <CardHeader className="align-items-center d-flex">
        <h4 className="card-title mb-0 flex-grow-1">
          <b>Heat Map</b>
        </h4>
        <div className="flex-shrink-0">
          <UncontrolledDropdown className="card-header-dropdown">
            <DropdownToggle tag="a" className="text-reset dropdown-btn" role="button">
              <span className="text-muted">
                {gridType}
                <i className="mdi mdi-chevron-down ms-1"></i>
              </span>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu dropdown-menu-end">
              {["HOME", "WORK", "WEEKEND", "WEEKDAY"].map((type) => (
                <DropdownItem key={type} onClick={() => handleSelectionChange(type)}>
                  {type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </CardHeader>
      <CardBody>
        {!isResetting && (
          <KeplerGl
            key={mapKey}
            id={`heat-map-${gridType}`}
            width={window.innerWidth * 0.85}
            height={500}
            mapboxApiAccessToken={"pk.eyJ1IjoiYnVyYWt0b3N1bnZlbG94aXR5IiwiYSI6ImNtNDcwenNlMTAwa2cyb3M5Y2hzcmZpMDEifQ.VmNGOl4oq1qJGN0yNMmFGA"}
          />
        )}
      </CardBody>
    </Card>
  );
};

export default HeatMapCard;
